import React from 'react';
import { FonctionArmement, Competence, CompetenceFonctionArmement } from '../../../../../../shared/types/config';

interface FonctionsArmementTabProps {
  fonctionsArmement: FonctionArmement[];
  searchFonctionsArmement: string;
  onSearchChange: (value: string) => void;
  onAdd: () => void;
  onEdit: (fonctionArmement: FonctionArmement) => void;
  onDelete: (id: number) => void;
  competences: Competence[];
}

const FonctionsArmementTab: React.FC<FonctionsArmementTabProps> = ({
  fonctionsArmement,
  searchFonctionsArmement,
  onSearchChange,
  onAdd,
  onEdit,
  onDelete,
  competences,
}) => {
  const filteredFonctionsArmement = fonctionsArmement.filter(f => {
    const matchesSearch = f.nom.toLowerCase().includes(searchFonctionsArmement.toLowerCase()) ||
      (f.description || '').toLowerCase().includes(searchFonctionsArmement.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Barre de recherche et bouton ajouter */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Rechercher une fonction d'armement..."
            value={searchFonctionsArmement}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <svg className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <button
          onClick={onAdd}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter
        </button>
      </div>

      {/* Liste des fonctions d'armement */}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-gray-800 border-b border-gray-700">
            <tr>
              <th className="px-4 py-3 text-gray-300 font-semibold">ID</th>
              <th className="px-4 py-3 text-gray-300 font-semibold">Nom</th>
              <th className="px-4 py-3 text-gray-300 font-semibold">Description</th>
              <th className="px-4 py-3 text-gray-300 font-semibold">Compétences</th>
              <th className="px-4 py-3 text-gray-300 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFonctionsArmement.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                  Aucune fonction d'armement trouvée
                </td>
              </tr>
            ) : (
              filteredFonctionsArmement.map((fonctionArmement) => {
                // Gérer le nouveau format : tableau d'objets {id, ordre, optionnel, ref}
                let competencesData: Array<{id: number, nom: string, ordre: number, optionnel: boolean, ref: boolean}> = [];
                if (fonctionArmement.id_competences && Array.isArray(fonctionArmement.id_competences)) {
                  if (fonctionArmement.id_competences.length > 0) {
                    const firstItem = fonctionArmement.id_competences[0];
                    if (typeof firstItem === 'number') {
                      // Ancien format : [1, 2, 3]
                      competencesData = (fonctionArmement.id_competences as unknown as number[])
                        .map((id, index) => ({
                          id,
                          nom: competences.find(c => c.id === id)?.nom || `ID: ${id}`,
                          ordre: index + 1,
                          optionnel: false,
                          ref: false
                        }))
                        .filter(c => c.nom);
                    } else if (typeof firstItem === 'object' && firstItem !== null && 'id' in firstItem && 'ordre' in firstItem) {
                      // Nouveau format : [{id: 1, ordre: 1, optionnel: false, ref: false}, {id: 2, ordre: 2, optionnel: true, ref: false}, ...]
                      competencesData = (fonctionArmement.id_competences as CompetenceFonctionArmement[])
                        .map(comp => ({
                          id: comp.id,
                          nom: competences.find(c => c.id === comp.id)?.nom || `ID: ${comp.id}`,
                          ordre: comp.ordre,
                          optionnel: comp.optionnel || false,
                          ref: comp.ref || false
                        }))
                        .filter(c => c.nom)
                        .sort((a, b) => a.ordre - b.ordre);
                    }
                  }
                }
                return (
                  <tr key={fonctionArmement.id} className="border-b border-gray-700 hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-gray-300">{fonctionArmement.id}</td>
                    <td className="px-4 py-3 text-white font-medium">{fonctionArmement.nom}</td>
                    <td className="px-4 py-3 text-gray-300 max-w-xs truncate">
                      {fonctionArmement.description || '-'}
                    </td>
                    <td className="px-4 py-3">
                      {competencesData.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {competencesData.map((comp, index) => {
                            const statusLabels = [];
                            if (comp.optionnel) statusLabels.push('optionnel');
                            if (comp.ref) statusLabels.push('ref');
                            const statusText = statusLabels.length > 0 ? ` (${statusLabels.join(', ')})` : '';
                            return (
                              <span
                                key={index}
                                className={`px-2 py-1 rounded text-xs border ${
                                  comp.ref 
                                    ? 'bg-green-500/20 text-green-300 border-green-500/50'
                                    : comp.optionnel
                                    ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50'
                                    : 'bg-blue-500/20 text-blue-300 border-blue-500/50'
                                }`}
                                title={`Ordre: ${comp.ordre}${statusText}`}
                              >
                                {comp.ordre}. {comp.nom}{statusText}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-gray-500 text-xs">Aucune</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onEdit(fonctionArmement)}
                          className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                          title="Modifier"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => fonctionArmement.id && onDelete(fonctionArmement.id)}
                          className="p-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FonctionsArmementTab;

